// auth/msal.ts
import { SignInFailedError, type SignInErrorDetail } from '@/auth/signInFailure';
import { AuthError, LogLevel, PublicClientApplication } from '@azure/msal-browser';

// Entra ID, Zeres b.v. workforce tenant (zeresnl.onmicrosoft.com). Overridable per environment;
// the defaults are the "Zeres Backoffice App" SPA registration and the InternalApi's api.all
// scope, the same pair the InternalApi's Scalar page signs in with. The marcelzeres tenant these
// previously pointed at is being retired; see design/plans/260829 Tenant Restructuring.md.
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID || '7a09fe18-f86f-4b25-89f9-ca0e4e7e5c6e';
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID || '001cc9c6-5c27-420f-ad68-77e99199a9a8';
const apiScope = import.meta.env.VITE_ENTRA_API_SCOPE || 'api://89bc6a3f-7da8-46a3-8265-68017ccdab99/api.all';

export const apiScopes = [apiScope];

const logLevelKey = 'zeres.msalLogLevel';
const logLevels: Record<string, LogLevel> = {
    error: LogLevel.Error,
    warning: LogLevel.Warning,
    info: LogLevel.Info,
    verbose: LogLevel.Verbose
};

// `?msalLog=verbose` raises MSAL's own logging for the rest of the tab and `?msalLog=off` puts it
// back. The level is kept in sessionStorage rather than read from the URL on every call, because
// signing in navigates to Entra and back and the query string does not survive that. An
// environment variable could not do this job: Vite inlines VITE_ values at build time, so raising
// the level on a deployed site would mean a rebuild and a deploy, and these failures are the kind
// you want to look at while the page in front of you is still broken.
function configuredLogLevel(): LogLevel {
    const requested = new URLSearchParams(window.location.search).get('msalLog')?.toLowerCase();
    if (requested === 'off') {
        sessionStorage.removeItem(logLevelKey);
    } else if (requested && requested in logLevels) {
        sessionStorage.setItem(logLevelKey, requested);
    }

    return logLevels[sessionStorage.getItem(logLevelKey) ?? ''] ?? LogLevel.Warning;
}

export const msalInstance = new PublicClientApplication({
    auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        // redirect.html, not the app. MSAL v5 relays a popup or hidden-iframe response through the
        // page at the redirect URI and never through this bundle, so pointing this at the SPA
        // leaves silent renewal waiting for an answer that never comes.
        // See design/plans/260920 MSAL Redirect Bridge.md.
        redirectUri: `${window.location.origin}/redirect.html`,
        // logoutRedirect otherwise sends Entra's post-logout redirect to the page the user signed
        // out from. That page boots the app, finds no account and bounces straight back into
        // sign-in, so the root is the saner landing spot. It also used to answer 404 on the
        // published site, which no longer applies.
        postLogoutRedirectUri: window.location.origin
    },
    cache: {
        cacheLocation: 'sessionStorage'
    },
    system: {
        loggerOptions: {
            logLevel: configuredLogLevel(),
            // MSAL skips the callback entirely for anything it marks as PII, so no token, code or
            // claim reaches the console and this is safe to leave on in production.
            piiLoggingEnabled: false,
            // MSAL's own default callback discards everything, so without one the level is inert.
            loggerCallback: (level, message) => {
                const write = level === LogLevel.Error ? console.error : level === LogLevel.Warning ? console.warn : console.info;
                write(`[msal] ${message}`);
            }
        }
    }
});

let initialized = false;

// One redirect at a time. The dashboard loads its cards in parallel, so a session that can no
// longer produce a token fails six requests at once, and six calls to acquireTokenRedirect make
// MSAL let the first through and throw interaction_in_progress at the rest, which arrives as a row
// of error toasts over a page that is already navigating away. The first caller drives the
// redirect and the others await the same promise. A rejection clears it so a later attempt is not
// stuck with a poisoned one.
let signInRedirect: Promise<never> | null = null;

// No redirectStartPage: MSAL caches the page the sign-in started from and returns the browser
// there, which is what we want. It was pinned to the origin for a while because returning to any
// route but "/" answered 404 on the published site, and the sign-in dead-ended on the page the
// user came from. The site rewrites unmatched paths to index.html now, so the return works and
// the pin only cost people their place.
function redirectToSignIn(): Promise<never> {
    signInRedirect ??= msalInstance
        .acquireTokenRedirect({ scopes: apiScopes })
        // acquireTokenRedirect navigates away; keep callers suspended until it does
        .then(() => new Promise<never>(() => {}))
        .catch((error: unknown) => {
            signInRedirect = null;
            throw error;
        });

    return signInRedirect;
}

// Entra reports a refused sign-in in the redirect it sends back, and that response now lands on
// redirect.html, which caches it and brings the browser here with a clean URL. So there is nothing
// left to read off the fragment: handleRedirectPromise finds the cached payload and rejects with
// what Entra said. ServerError and InteractionRequiredAuthError are built from `error`,
// `error_description` and `correlation_id`, after MSAL validated the state they arrived with.
function signInErrorDetail(error: unknown): SignInErrorDetail | null {
    if (!(error instanceof AuthError)) {
        return null;
    }

    return {
        error: error.errorCode,
        description: error.errorMessage || null,
        correlationId: error.correlationId || null,
        subError: error.subError || null
    };
}

export async function ensureSignedIn(): Promise<void> {
    if (!initialized) {
        await msalInstance.initialize();

        try {
            const response = await msalInstance.handleRedirectPromise();
            if (response) {
                //the account that came back, so a mismatch between who signed in and what the API
                //refuses is visible; no token and no claims are logged
                console.info('[msal] Entra sign-in completed for', response.account?.username);
            }
        } catch (error) {
            console.error('[msal] Processing the Entra redirect failed', error);
            const detail = signInErrorDetail(error);
            throw detail ? new SignInFailedError(detail) : error;
        }

        initialized = true;
    }

    if (msalInstance.getAllAccounts().length === 0) {
        await redirectToSignIn();
    }
}

// A renewal that cannot be resolved leaves the app holding an account it cannot use: ensureSignedIn
// sees that account and never redirects, so reloading changes nothing and every screen keeps
// failing until the tab closes and sessionStorage goes with it. One sign-in gets out of that. The
// marker is what stops a failure that survives the round trip from bouncing through Entra forever.
const renewalRedirectKey = 'zeres.tokenRenewalRedirect';

export async function acquireApiToken(): Promise<string> {
    const account = msalInstance.getAllAccounts()[0];
    if (!account) {
        return redirectToSignIn();
    }

    try {
        const result = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account });
        sessionStorage.removeItem(renewalRedirectKey);
        return result.accessToken;
    } catch (error) {
        // Every failure takes the same route now, InteractionRequiredAuthError included. That
        // one used to redirect unguarded, which was safe while the iframe never answered; with
        // the bridge in place it is the common way a dead session reports itself, and a session
        // that comes back from Entra still unable to produce a token would bounce forever. So
        // one redirect per session, and the second failure is reported instead.
        if (sessionStorage.getItem(renewalRedirectKey)) {
            throw error;
        }

        console.error('[msal] Renewing the access token failed, signing in again', error);
        sessionStorage.setItem(renewalRedirectKey, new Date().toISOString());
        return redirectToSignIn();
    }
}

export function signOut(): Promise<void> {
    return msalInstance.logoutRedirect();
}
